import { useEffect, useMemo, useState } from 'react';

export interface UseGlassWidgetCompactViewportOptions {
  /**
   * Used to build `(max-height: Npx)` for `matchMedia` when `compactViewportMediaQuery` is omitted.
   * Call sites should match HomeView ribbon SCSS (e.g. `height <= 899px`).
   */
  compactViewportMaxHeightPx: number;
  /** Card-slot budget for the glass widget carousel when the compact media query matches. */
  compactMaxVisibleGlassCardSlots: number;
  /**
   * Optional full media query for `matchMedia`. When set, overrides the height-based query; you must
   * keep related layout SCSS / breakpoints in sync manually.
   */
  compactViewportMediaQuery?: string;
}

/**
 * Subscribes to a viewport media query and reports whether the glass widget stack should use **compact**
 * mode (limited card slots) vs **full** mode (all cards visible, no middle pagination).
 *
 * Use the returned `maxVisibleGlassWidgetCardSlots` as `maxVisible` on `GlassWidgetCardCarousel` (or
 * equivalent): when compact, pins + middle share the slot budget; when not compact, `Infinity` means
 * show every middle card at once.
 *
 * @param options - Breakpoint height and slot budget; optional full `matchMedia` string overrides the
 *   default `(max-height: …px)` query (keep layout SCSS aligned if you override).
 * @returns Compact flag and effective max visible slots for the carousel.
 */
export interface UseGlassWidgetCompactViewportResult {
  /** `true` when the active compact media query matches (short viewport for the glass stack). */
  isGlassWidgetCompactViewport: boolean;
  /** `compactMaxVisibleGlassCardSlots` when compact, otherwise unlimited (`Number.POSITIVE_INFINITY`). */
  maxVisibleGlassWidgetCardSlots: number;
}

export function useGlassWidgetCompactViewport(
  options: UseGlassWidgetCompactViewportOptions,
): UseGlassWidgetCompactViewportResult {
  const { compactViewportMaxHeightPx, compactMaxVisibleGlassCardSlots } = options;

  const mediaQuery = useMemo(() => {
    const override = options.compactViewportMediaQuery;
    if (override !== undefined && override !== '') {
      return override;
    }
    return `(max-height: ${compactViewportMaxHeightPx}px)`;
  }, [options.compactViewportMediaQuery, compactViewportMaxHeightPx]);

  const [isGlassWidgetCompactViewport, setIsGlassWidgetCompactViewport] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(mediaQuery).matches : false,
  );

  useEffect(() => {
    const mq = window.matchMedia(mediaQuery);
    const syncCompactViewport = () => {
      setIsGlassWidgetCompactViewport(mq.matches);
    };
    syncCompactViewport();
    mq.addEventListener('change', syncCompactViewport);
    return () => {
      mq.removeEventListener('change', syncCompactViewport);
    };
  }, [mediaQuery]);

  const maxVisibleGlassWidgetCardSlots = isGlassWidgetCompactViewport
    ? compactMaxVisibleGlassCardSlots
    : Number.POSITIVE_INFINITY;

  return { isGlassWidgetCompactViewport, maxVisibleGlassWidgetCardSlots };
}
